/**
 * Mobile viewport + virtual keyboard fixes (imported once from index.js).
 *
 * Problem: on iOS Safari / Android Chrome the virtual keyboard shrinks the
 * *visual* viewport but `100vh` (and, on iOS, even `window.innerHeight`)
 * doesn't reflect that. Fixed-position sheets/drawers sized with vh end up
 * taller than the visible area, so form fields "vanish" behind the keyboard.
 *
 * Fix 1 — `--app-height` CSS variable kept in sync with the real visible
 * height via `visualViewport` (falls back to `window.innerHeight`). Any
 * container sized with `var(--app-height)` (drawers, dialogs, .App) resizes
 * live when the keyboard opens/closes or the device rotates.
 *
 * Fix 2 — gently scroll the focused field into view on small screens after
 * the keyboard has animated in, so the active input is never hidden.
 *
 * Desktop is untouched: the variable simply equals the normal viewport
 * height there, and the focus handler exits immediately above 768px.
 */

const MOBILE_MAX_WIDTH = 768;

function setAppHeight() {
  const height = window.visualViewport
    ? Math.round(window.visualViewport.height)
    : window.innerHeight;
  document.documentElement.style.setProperty('--app-height', `${height}px`);
}

function handleMobileInputFocus(event) {
  if (window.innerWidth > MOBILE_MAX_WIDTH) return;
  const input = event.target;
  if (!input || !['INPUT', 'SELECT', 'TEXTAREA'].includes(input.tagName)) return;
  // Give the virtual keyboard time to open (and the viewport to resize)
  // before scrolling, otherwise we scroll to a stale position.
  window.setTimeout(() => {
    try {
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
      // Very old browsers without options support — plain fallback.
      input.scrollIntoView();
    }
  }, 300);
}

export function installMobileViewportFixes() {
  setAppHeight();
  window.addEventListener('resize', setAppHeight);
  window.addEventListener('orientationchange', setAppHeight);
  if (window.visualViewport) {
    // The keyboard opening does NOT fire window.resize on iOS — but it does
    // fire visualViewport.resize. This is the one that matters most.
    window.visualViewport.addEventListener('resize', setAppHeight);
  }
  document.addEventListener('focusin', handleMobileInputFocus);
}
